import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";

import config from "@/config";
import usePaymentProfile from "@/hooks/usePaymentProfile";
import useTranslations from "@/i18n/useTranslations";
import paymentProfileService from "@/services/paymentProfile.service";
import ContinueButton from "@/shared-components/buttons/components/ContinueButton";
import InfoIcon from "@/shared-components/icons/components/public/InfoIcon";
import XIcon from "@/shared-components/icons/components/public/XIcon";
import PublicFormInput from "@/shared-components/inputs/components/PublicFormInput";
import GenericPopup from "@/shared-components/popups/components/GenericPopup";
import Popup from "@/shared-components/popups/components/Popup";

import "./styles/BillingDetailsCard.css";

function createEmptyBillingAddress() {
  return {
    address_country: "",
    address_street: "",
    address_extra: "",
    address_city: "",
    address_region: "",
    address_zip_code: "",
  };
}

function createEmptyBillingInfo() {
  return {
    is_business: false,
    invoice_requested: false,
    legal_name: "",
    vat_id: "",
    vat_valid: null,
    vat_country: "",
    vat_name: "",
    vat_address: "",
    tax_id: "",
  };
}

function hydrateBillingAddress(source = null) {
  const fallback = createEmptyBillingAddress();
  if (!source || typeof source !== "object") {
    return fallback;
  }

  return {
    ...fallback,
    address_country: source.address_country ?? "",
    address_street: source.address_street ?? "",
    address_extra: source.address_extra ?? "",
    address_city: source.address_city ?? "",
    address_region: source.address_region ?? "",
    address_zip_code: source.address_zip_code ?? "",
  };
}

function hydrateBillingInfo(source = null) {
  const fallback = createEmptyBillingInfo();
  if (!source || typeof source !== "object") {
    return fallback;
  }

  return {
    ...fallback,
    is_business: Boolean(source.is_business),
    invoice_requested: typeof source.invoice_requested === "boolean"
      ? source.invoice_requested
      : Boolean(source.is_business),
    legal_name: source.legal_name ?? source.company_name ?? "",
    vat_id: source.vat_id ?? "",
    vat_valid: typeof source.vat_valid === "boolean" ? source.vat_valid : null,
    vat_country: source.vat_country ?? "",
    vat_name: source.vat_name ?? "",
    vat_address: source.vat_address ?? "",
    tax_id: source.tax_id ?? "",
  };
}

function hasAddressDetails(address) {
  if (!address || typeof address !== "object") {
    return false;
  }

  return [
    address.address_street,
    address.address_extra,
    address.address_city,
    address.address_region,
    address.address_zip_code,
    address.address_country,
  ].some((value) => typeof value === "string" && value.trim());
}

function formatAddress(address) {
  if (!hasAddressDetails(address)) {
    return [];
  }

  const lineOne = [address.address_street, address.address_extra]
    .filter((value) => typeof value === "string" && value.trim())
    .join(", ");

  const lineTwo = [address.address_zip_code, address.address_city]
    .filter((value) => typeof value === "string" && value.trim())
    .join(" ");

  const lineThree = [address.address_region, address.address_country]
    .filter((value) => typeof value === "string" && value.trim())
    .join(", ");

  return [lineOne, lineTwo, lineThree].filter(Boolean);
}

function normaliseVatId(value) {
  return typeof value === "string" ? value.trim().toUpperCase() : "";
}

export default function BillingDetailsCard({
  title = null,
  description = null,
  billingAddress = null,
  billingInfo = null,
  shippingAddress = null,
  onSaved = null,
  className = "",
  hideInfoIcon = false,
  triggerOnly = false,
}) {
  const t = useTranslations("common.shared.billing_details");
  const tx = useCallback((key, fallback, values) => {
    const translated = t(key, values);
    return translated === key ? fallback : translated;
  }, [t]);
  const resolvedTitle = title ?? tx("title", "Billing details");
  const billingInfoText = description ?? tx(
    "description",
    "Billing details are used on invoices and for VAT treatment when applicable. This is optional unless you need an invoice.",
  );

  const paymentProfile = usePaymentProfile();
  const saveBillingDetails = paymentProfile?.saveBillingDetails;
  const profileAddress = paymentProfile?.profile?.billing_address ?? null;
  const profileInfo = paymentProfile?.profile?.billing_info ?? null;

  const effectiveAddress = billingAddress ?? profileAddress ?? null;
  const effectiveInfo = billingInfo ?? profileInfo ?? null;

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [billingInfoOpen, setBillingInfoOpen] = useState(false);
  const [vatVerifying, setVatVerifying] = useState(false);
  const [error, setError] = useState(null);
  const [vatMessage, setVatMessage] = useState(null);
  const [vatMessageType, setVatMessageType] = useState(null);
  const [draftAddress, setDraftAddress] = useState(() => hydrateBillingAddress(effectiveAddress));
  const [draftInfo, setDraftInfo] = useState(() => hydrateBillingInfo(effectiveInfo));

  useEffect(() => {
    if (open) {
      return;
    }
    setDraftAddress(hydrateBillingAddress(effectiveAddress));
    setDraftInfo(hydrateBillingInfo(effectiveInfo));
    setVatMessage(null);
    setVatMessageType(null);
  }, [effectiveAddress, effectiveInfo, open]);

  const addressLines = useMemo(() => formatAddress(effectiveAddress), [effectiveAddress]);
  const hasFiscalInfo = Boolean(
    effectiveInfo?.is_business ||
    effectiveInfo?.invoice_requested ||
    (typeof effectiveInfo?.vat_id === "string" && effectiveInfo.vat_id.trim()) ||
    (typeof effectiveInfo?.tax_id === "string" && effectiveInfo.tax_id.trim()) ||
    (typeof effectiveInfo?.legal_name === "string" && effectiveInfo.legal_name.trim())
  );
  const hasSavedBillingDetails = addressLines.length > 0 || hasFiscalInfo;

  const handleAddressChange = useCallback((key, value) => {
    setDraftAddress((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const handleInfoChange = useCallback((key, value) => {
    setDraftInfo((prev) => {
      const next = {
        ...prev,
        [key]: value,
      };

      if (key === "is_business" && !value) {
        next.invoice_requested = false;
        next.vat_valid = null;
        next.vat_country = "";
        next.vat_name = "";
        next.vat_address = "";
      }

      if (key === "vat_id") {
        next.vat_valid = null;
        next.vat_country = "";
        next.vat_name = "";
        next.vat_address = "";
      }

      return next;
    });
    if (key === "vat_id") {
      setVatMessage(null);
      setVatMessageType(null);
    }
  }, []);

  const handleCopyShipping = useCallback(() => {
    if (!shippingAddress || typeof shippingAddress !== "object") {
      return;
    }

    setDraftAddress((prev) => ({
      ...prev,
      address_country: shippingAddress.address_country ?? "",
      address_street: shippingAddress.address_street ?? "",
      address_extra: shippingAddress.address_extra ?? "",
      address_city: shippingAddress.address_city ?? "",
      address_region: shippingAddress.address_region ?? "",
      address_zip_code: shippingAddress.address_zip_code ?? "",
    }));
  }, [shippingAddress]);

  const buildPayloadInfo = useCallback(() => ({
    is_business: Boolean(draftInfo.is_business),
    invoice_requested: Boolean(draftInfo.invoice_requested || draftInfo.is_business),
    legal_name: draftInfo.legal_name?.trim?.() ?? "",
    company_name: draftInfo.legal_name?.trim?.() ?? "",
    vat_id: normaliseVatId(draftInfo.vat_id),
    vat_valid: typeof draftInfo.vat_valid === "boolean" ? draftInfo.vat_valid : null,
    vat_country: draftInfo.vat_country?.trim?.() ?? "",
    vat_name: draftInfo.vat_name?.trim?.() ?? "",
    vat_address: draftInfo.vat_address?.trim?.() ?? "",
    tax_id: draftInfo.tax_id?.trim?.() ?? "",
  }), [draftInfo]);

  const buildPayloadAddress = useCallback(() => ({
    address_country: draftAddress.address_country ?? "",
    address_street: draftAddress.address_street ?? "",
    address_extra: draftAddress.address_extra ?? "",
    address_city: draftAddress.address_city ?? "",
    address_region: draftAddress.address_region ?? "",
    address_zip_code: draftAddress.address_zip_code ?? "",
  }), [draftAddress]);

  const requiresVatVerification = useMemo(() =>
    Boolean(
      draftInfo?.is_business === true
      && normaliseVatId(draftInfo?.vat_id)
    ),
  [draftInfo?.is_business, draftInfo?.vat_id]);

  const canSaveBillingDetails = useMemo(() =>
    !requiresVatVerification || draftInfo?.vat_valid === true,
  [draftInfo?.vat_valid, requiresVatVerification]);

  const handleVerifyVat = useCallback(async () => {
    const vatId = normaliseVatId(draftInfo.vat_id);
    const billingAddress = buildPayloadAddress();
    if (!vatId) {
      return;
    }
    if (!billingAddress.address_country.trim()) {
      setVatMessage(tx("errors.country_required", "Select a country before verifying a VAT ID."));
      setVatMessageType("error");
      return;
    }

    setVatVerifying(true);
    setVatMessage(null);
    setVatMessageType(null);

    try {
      const { response, data } = await paymentProfileService.verifyVatId({
        vatId,
        billingAddress,
        billingInfo: buildPayloadInfo(),
      });

      if (!response?.ok) {
        throw new Error(data?.error_message ?? tx("errors.vat_verify_failed", "VAT ID could not be verified."));
      }

      setDraftInfo((prev) => ({
        ...prev,
        vat_id: vatId,
        vat_valid: true,
        vat_country: data?.vat_country ?? prev.vat_country ?? "",
      }));
      setVatMessage(tx("vat.verified_info", "VAT ID verified. Reverse charge may apply where eligible."));
      setVatMessageType("success");
    }
    catch (vatError) {
      setDraftInfo((prev) => ({
        ...prev,
        vat_valid: false,
        vat_country: "",
        vat_name: "",
        vat_address: "",
      }));
      setVatMessage(vatError?.message ?? tx("errors.vat_verify_failed", "VAT ID could not be verified."));
      setVatMessageType("error");
    }
    finally {
      setVatVerifying(false);
    }
  }, [buildPayloadAddress, buildPayloadInfo, draftInfo.vat_id, tx]);

  const handleSave = useCallback(async (event) => {
    event.preventDefault();
    if (typeof saveBillingDetails !== "function") {
      return;
    }

    const payloadInfo = buildPayloadInfo();
    const payloadAddress = buildPayloadAddress();
    const hasBillingDetails = hasAddressDetails(payloadAddress) || Object.values(payloadInfo)
      .some((value) => typeof value === "string" ? Boolean(value.trim()) : Boolean(value));

    if (hasBillingDetails && !payloadAddress.address_country.trim()) {
      setError(tx("errors.country_required", "Select a country before saving billing details."));
      return;
    }

    if (!canSaveBillingDetails) {
      setError(tx(
        "errors.vat_must_be_verified",
        "Verify a valid VAT ID before saving business billing details.",
      ));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await saveBillingDetails({
        billingAddress: payloadAddress,
        billingInfo: payloadInfo,
        country: payloadAddress.address_country || undefined,
      });

      onSaved?.({
        billingAddress: payloadAddress,
        billingInfo: payloadInfo,
      });
      setOpen(false);
    }
    catch (saveError) {
      setError(saveError?.message ?? tx("errors.save_failed", "Unable to save billing details."));
    }
    finally {
      setSaving(false);
    }
  }, [buildPayloadAddress, buildPayloadInfo, canSaveBillingDetails, onSaved, saveBillingDetails, tx]);

  return (
    <>
      {triggerOnly ? (!hasSavedBillingDetails ? (
        <section className={`add-billing-card add-billing-card--minimal ${className}`.trim()}>
          <button
            type="button"
            className="add-billing-card__minimal-action"
            onClick={() => setOpen(true)}
          >
            {tx("actions.add_billing", "Add billing")}
          </button>
        </section>
      ) : null) : (
        <section className={`add-billing-card ${className}`.trim()}>
          <div className="add-billing-card__header">
            <div>
              <div className="add-billing-card__title-row">
                <h3 className="add-billing-card__title">{resolvedTitle}</h3>
                {!hideInfoIcon ? (
                  <span className="purchase-info__detail-info">
                    <button
                      type="button"
                      className="purchase-info__detail-info-button"
                      onClick={() => setBillingInfoOpen(true)}
                      aria-label={tx("labels.learn_more_billing", "Learn more about billing details")}
                      aria-expanded={billingInfoOpen}
                      aria-haspopup="dialog"
                    >
                      <InfoIcon className="purchase-info__detail-info-icon" />
                    </button>
                  </span>
                ) : null}
              </div>
            </div>

            {hasSavedBillingDetails ? (
              <ContinueButton
                type="button"
                className="add-billing-card__action"
                onClick={() => setOpen(true)}
              >
                {tx("actions.edit", "Edit")}
              </ContinueButton>
            ) : null}
          </div>

          {hasSavedBillingDetails ? (
            <div className="add-billing-card__summary">
              {addressLines.length > 0 ? (
                <div className="add-billing-card__summary-block">
                  <span className="add-billing-card__summary-label">{tx("summary.billing_address", "Billing address")}</span>
                  {addressLines.map((line) => (
                    <p key={line} className="add-billing-card__summary-line">{line}</p>
                  ))}
                </div>
              ) : null}

              <div className="add-billing-card__summary-block">
                <span className="add-billing-card__summary-label">{tx("summary.business_details", "Business details")}</span>
                {effectiveInfo?.legal_name ? (
                  <p className="add-billing-card__summary-line">{effectiveInfo.legal_name}</p>
                ) : null}
                {effectiveInfo?.vat_id ? (
                  <p className="add-billing-card__summary-line">
                    {tx("summary.vat_id", "VAT ID")}: {effectiveInfo.vat_id}
                  </p>
                ) : null}
                {effectiveInfo?.tax_id ? (
                  <p className="add-billing-card__summary-line">{tx("summary.company_registration", "Company registration")}: {effectiveInfo.tax_id}</p>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="add-billing-card__empty-state">
              <p className="add-billing-card__empty">
                {tx("summary.empty", "Add the billing address for invoices")}
              </p>
              <ContinueButton
                type="button"
                className="add-billing-card__action"
                onClick={() => setOpen(true)}
              >
                {tx("actions.add", "Add")}
              </ContinueButton>
            </div>
          )}
        </section>
      )}

      <Popup
        open={billingInfoOpen}
        close={() => setBillingInfoOpen(false)}
        title={resolvedTitle}
        className="generic-popup_content"
      >
        <div className="add-billing-card__info-popup-content">
          <p className="add-billing-card__info-popup-copy">{billingInfoText}</p>
        </div>
      </Popup>

      <GenericPopup
        open={open}
        className="add-billing-card__modal remove-scrollbar"
        backgroundClassName="add-billing-card__overlay"
        opacity
        onBackgroundClick={() => setOpen(false)}
      >
        <form className="add-billing-card__form" onSubmit={handleSave}>
          <div className="generic-popup__header">
            <h3 className="generic-popup__header-text">{tx("modal.title", "Billing details")}</h3>
            <XIcon className="generic-popup__close" onClick={() => setOpen(false)} />
          </div>

          <div className="add-billing-card__modal-description">
            <p className="add-billing-card__modal-copy">
              {tx("modal.description", "Save the billing address and fiscal details you want to use for invoices and tax treatment.")}
            </p>
          </div>

          <div className="add-billing-card__grid add-billing-card__grid--two">
            <PublicFormInput
              type="select"
              placeholder={tx("fields.country", "Country")}
              element="billing_address_country"
              value={draftAddress.address_country}
              onChange={(value) => handleAddressChange("address_country", value)}
              selectOptions={{ options: config.forms.prefix_options, type: "country", searchable: true }}
            />
            <PublicFormInput
              placeholder={tx("fields.postal_code", "Postal code")}
              element="billing_address_zip_code"
              value={draftAddress.address_zip_code}
              onChange={(event) => handleAddressChange("address_zip_code", event.target.value)}
            />
          </div>

          <PublicFormInput
            placeholder={tx("fields.street", "Street address")}
            element="billing_address_street"
            value={draftAddress.address_street}
            onChange={(event) => handleAddressChange("address_street", event.target.value)}
          />

          <PublicFormInput
            placeholder={tx("fields.address_extra", "Address line 2 (optional)")}
            element="billing_address_extra"
            value={draftAddress.address_extra}
            onChange={(event) => handleAddressChange("address_extra", event.target.value)}
          />

          <div className="add-billing-card__grid add-billing-card__grid--two">
            <PublicFormInput
              placeholder={tx("fields.city", "City")}
              element="billing_address_city"
              value={draftAddress.address_city}
              onChange={(event) => handleAddressChange("address_city", event.target.value)}
            />
            <PublicFormInput
              placeholder={tx("fields.region", "Region / State")}
              element="billing_address_region"
              value={draftAddress.address_region}
              onChange={(event) => handleAddressChange("address_region", event.target.value)}
            />
          </div>

          {shippingAddress ? (
            <button
              type="button"
              className="add-billing-card__link"
              onClick={handleCopyShipping}
            >
              {tx("actions.use_shipping", "Use shipping address")}
            </button>
          ) : null}

          <PublicFormInput
            type="checkbox"
            element="billing_is_business"
            value={Boolean(draftInfo.is_business)}
            onChange={(value) => handleInfoChange("is_business", value)}
            className="add-billing-card__checkbox"
          >
            {tx("fields.is_business", "I am purchasing as a business")}
          </PublicFormInput>

          {draftInfo.is_business ? (
            <div className="add-billing-card__tax-section">
              <PublicFormInput
                placeholder={tx("fields.legal_name", "Company legal name")}
                element="billing_legal_name"
                value={draftInfo.legal_name}
                onChange={(event) => handleInfoChange("legal_name", event.target.value)}
              />

              <div className="add-billing-card__grid add-billing-card__grid--two">
                <PublicFormInput
                  placeholder={tx("fields.vat_id", "VAT ID")}
                  element="billing_vat_id"
                  value={draftInfo.vat_id}
                  onChange={(event) => handleInfoChange("vat_id", event.target.value)}
                />
                <PublicFormInput
                  placeholder={tx("fields.tax_id", "Company registration number (optional)")}
                  element="billing_tax_id"
                  value={draftInfo.tax_id}
                  onChange={(event) => handleInfoChange("tax_id", event.target.value)}
                />
              </div>

              {vatMessage ? (
                <p className={`add-billing-card__vat-status add-billing-card__vat-status--${vatMessageType || "info"}`}>
                  {vatMessage}
                </p>
              ) : draftInfo.vat_valid === true ? (
                <p className="add-billing-card__vat-status add-billing-card__vat-status--success">
                  {tx("vat.verified_business", "VAT ID verified. Business purchase detected.")}
                </p>
              ) : null}

              <ContinueButton
                type="button"
                className="add-billing-card__verify"
                disabled={!normaliseVatId(draftInfo.vat_id) || vatVerifying}
                onClick={handleVerifyVat}
              >
                {vatVerifying
                  ? tx("actions.verifying_vat", "Verifying VAT ID...")
                  : tx("actions.verify_vat", "Verify VAT ID")}
              </ContinueButton>
            </div>
          ) : null}

          {error ? (
            <p className="add-billing-card__error" role="alert">{error}</p>
          ) : null}

          <div className="add-billing-card__actions">
            <ContinueButton
              type="submit"
              className="add-billing-card__submit"
              loading={saving}
              disabled={!canSaveBillingDetails}
            >
              {tx("actions.save", "Save billing details")}
            </ContinueButton>
          </div>
        </form>
      </GenericPopup>
    </>
  );
}

BillingDetailsCard.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  billingAddress: PropTypes.object,
  billingInfo: PropTypes.object,
  shippingAddress: PropTypes.object,
  onSaved: PropTypes.func,
  className: PropTypes.string,
  hideInfoIcon: PropTypes.bool,
  triggerOnly: PropTypes.bool,
};
